import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { LogOut } from 'lucide-react';
import { useAuthStore } from 'stores/authStore';
import { useUserProfileStore } from 'stores/userProfileStore';
import { ROUTES } from 'utils/constants';
import Button from 'components/Button';
import styles from './CompleteProfile.module.scss';

function CompleteProfile() {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const profile = useUserProfileStore((s) => s.profile);
  const createProfile = useUserProfileStore((s) => s.createProfile);
  const refreshProfile = useUserProfileStore((s) => s.refreshProfile);
  const hasProfile = profile !== null;
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if profile already exists
  useEffect(() => {
    if (hasProfile) {
      // Check if user has companies
      if (profile?.companies && profile.companies.length > 0) {
        navigate(ROUTES.HOME);
      } else {
        navigate(ROUTES.COMPANY.SETUP);
      }
    }
  }, [hasProfile, profile?.companies, navigate]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const newProfile = await createProfile(name.trim());
      // Refresh profile to get the latest data including company info
      await refreshProfile();

      // Check company status - use the returned profile or refreshed profile
      // If no companies, redirect to lobby; otherwise redirect to home
      if (!newProfile.companies || newProfile.companies.length === 0) {
        navigate(ROUTES.COMPANY.SETUP);
      } else {
        navigate(ROUTES.HOME);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create profile';
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>Complete Your Profile</h1>
        </div>
        <div className={styles.body}>
          <p className={styles.description}>
            Please provide your name to complete your profile setup.
          </p>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit(e);
            }}
          >
            <Form.Group controlId="name" className={styles.formGroup}>
              <Form.Label className={styles.label}>Full Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={isSubmitting}
                autoFocus
                className={styles.input}
              />
            </Form.Group>
            {error && <div className={styles.errorMessage}>{error}</div>}
            <div className={styles.buttonContainer}>
              <Button
                type="button"
                variant="outline"
                onClick={() => void logout()}
              >
                <LogOut size={18} />
                <span>Logout</span>
              </Button>
              <Button type="submit" disabled={isSubmitting || !name.trim()}>
                {isSubmitting ? 'Creating...' : 'Complete Profile'}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default CompleteProfile;
